import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPaperPlane, FaUser, FaEnvelope, FaPhone, FaComment, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { useFirebase, useInteractionTracking, useSectionTracking } from '../hooks/useFirebase';
import { useForm, ValidationError } from '@formspree/react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // Formspree hook — handles sending + tracks state
  const [fsState, formspreeSubmit] = useForm('xykopzjr');

  const { contactService, analyticsService, loading: firebaseLoading, error: firebaseError } = useFirebase();
  const { trackClick, trackHover } = useInteractionTracking();
  const contactSectionRef = useSectionTracking('contact-section');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // 1. Track in Firebase analytics
      await analyticsService.trackFormSubmission('contact_form', true, {
        hasPhone: !!formData.phone,
        messageLength: formData.message.length
      });

      // 2. Save submission to Firestore
      await contactService.submitContact(formData);

      // 3. Send email notification via Formspree
      await formspreeSubmit({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || 'Not provided',
        message: formData.message,
      });

    } catch (error) {
      console.error('Form submission error:', error);
      await analyticsService.trackFormSubmission('contact_form', false, { error: error.message });
    }
  };

  return (
    <section id="contact" className="contact section" ref={contactSectionRef}>
      <span id="qr-contact" className="contact-anchor" aria-hidden="true"></span>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Get Involved</h2>
          <p className="section-subtitle">
            Whether you experience migraines and want to contribute, or you're a clinician or
            researcher who'd like to collaborate, we'd love to hear from you.
          </p>
        </motion.div>

        <div className="contact-content">
          <motion.div
            className="contact-form-container"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="contact-form-wrapper">
              {fsState.succeeded ? (
                <motion.div
                  className="success-message"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <FaCheckCircle className="success-icon" />
                  <h3>Thanks for reaching out!</h3>
                  <p>We'll get back to you within 24 hours with more details about getting involved.</p>
                </motion.div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      <FaUser className="label-icon" />
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      <FaEnvelope className="label-icon" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter your email address"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">
                      <FaPhone className="label-icon" />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message" className="form-label">
                      <FaComment className="label-icon" />
                      Tell us about yourself
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="form-textarea"
                      rows="5"
                      placeholder="How often do you get migraines? Are you a researcher or clinician? Any questions about the testing protocol?"
                      required
                    />
                  </div>

                  {firebaseError && (
                    <motion.div
                      className="error-message"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <FaExclamationTriangle className="error-icon" />
                      <span>{firebaseError}</span>
                    </motion.div>
                  )}

                  <motion.button
                    type="submit"
                    className="submit-button"
                    disabled={fsState.submitting || firebaseLoading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => trackClick('contact_form_submit', { formData: Object.keys(formData).filter(key => formData[key]) })}
                    onMouseEnter={() => trackHover('contact_form_submit')}
                  >
                    {fsState.submitting || firebaseLoading ? (
                      <div className="loading-spinner"></div>
                    ) : (
                      <>
                        <FaPaperPlane className="button-icon" />
                        Send Message
                      </>
                    )}
                  </motion.button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
